import { getFullName } from "@/entities/people/helpers";
import { PersonData } from "@/entities/people/types";
import { emailRegex } from "@/utils/validation";
import {
  ProposalRecipient,
  ProposalRecipientRole,
  ProposalRecipients,
  ProposalRecipientStatus
} from "../types";

// Someone who can be added, before a role is chosen for them.
export type RecipientCandidate = Omit<ProposalRecipient, "role">;

export const NO_RECIPIENTS: ProposalRecipients = { list: [], ordered: false };

// Every role, in the order they're offered and shown.
export const RECIPIENT_ROLES = Object.values(ProposalRecipientRole);

export const isValidEmail = (value: string) => emailRegex.test(value.trim());

export const sameEmail = (a: string, b: string) =>
  a.trim().toLowerCase() === b.trim().toLowerCase();

// A person with no email has nowhere to receive the request.
export const candidateFromPerson = (
  person: PersonData
): RecipientCandidate | undefined => {
  const email = person.email ?? person.emails?.[0];
  if (!email) return undefined;
  return {
    key: person.id,
    personId: person.id,
    name: getFullName(person) || email,
    email,
    company: person.companyName || undefined,
    jobTitle: person.jobTitle || undefined
  };
};

export const candidateFromEmail = (email: string): RecipientCandidate => {
  const trimmed = email.trim();
  return { key: trimmed.toLowerCase(), name: trimmed, email: trimmed };
};

// Whoever creates a proposal gets a copy of it unless they change that.
export const creatorRecipients = (creator: {
  displayName: string;
  email: string | null;
  jobTitle?: string;
}): ProposalRecipients => {
  const email = creator.email?.trim();
  if (!email) return NO_RECIPIENTS;
  return {
    list: [
      {
        key: email.toLowerCase(),
        name: creator.displayName || email,
        email,
        jobTitle: creator.jobTitle || undefined,
        role: ProposalRecipientRole.COPY
      }
    ],
    ordered: false
  };
};

export const signerCount = (list: ProposalRecipient[]) =>
  list.filter((recipient) => recipient.role === ProposalRecipientRole.SIGNER)
    .length;

export const hasRoomForSigner = (
  list: ProposalRecipient[],
  signaturePlaces: number
) => signerCount(list) < signaturePlaces;

// An order means something only between two or more signers.
export const canOrder = (list: ProposalRecipient[]) => signerCount(list) > 1;

const withList = (
  recipients: ProposalRecipients,
  list: ProposalRecipient[]
): ProposalRecipients => ({
  list,
  ordered: recipients.ordered && canOrder(list)
});

export const isAdded = (
  recipients: ProposalRecipients,
  candidate: RecipientCandidate
) =>
  recipients.list.some(
    (recipient) =>
      recipient.key === candidate.key ||
      sameEmail(recipient.email, candidate.email)
  );

// The newest recipient goes to the top of the list. They sign while the
// template has a place left, and get a copy after that.
export const addRecipient = (
  recipients: ProposalRecipients,
  candidate: RecipientCandidate,
  signaturePlaces: number
): ProposalRecipients => {
  if (isAdded(recipients, candidate)) return recipients;
  const role = hasRoomForSigner(recipients.list, signaturePlaces)
    ? ProposalRecipientRole.SIGNER
    : ProposalRecipientRole.COPY;
  return withList(recipients, [{ ...candidate, role }, ...recipients.list]);
};

export const setRecipientRole = (
  recipients: ProposalRecipients,
  key: string,
  role: ProposalRecipientRole,
  signaturePlaces: number
): ProposalRecipients => {
  const current = recipients.list.find((recipient) => recipient.key === key);
  if (!current || current.role === role) return recipients;
  if (
    role === ProposalRecipientRole.SIGNER &&
    !hasRoomForSigner(recipients.list, signaturePlaces)
  ) {
    return recipients;
  }
  return withList(
    recipients,
    recipients.list.map((recipient) =>
      recipient.key === key ? { ...recipient, role } : recipient
    )
  );
};

export const removeRecipient = (
  recipients: ProposalRecipients,
  key: string
): ProposalRecipients =>
  withList(
    recipients,
    recipients.list.filter((recipient) => recipient.key !== key)
  );

export const moveRecipient = (
  recipients: ProposalRecipients,
  from: number,
  to: number
): ProposalRecipients => {
  const list = [...recipients.list];
  const [moved] = list.splice(from, 1);
  if (!moved) return recipients;
  list.splice(to, 0, moved);
  return withList(recipients, list);
};

export const setOrdered = (
  recipients: ProposalRecipients,
  ordered: boolean
): ProposalRecipients => ({
  ...recipients,
  ordered: ordered && canOrder(recipients.list)
});

// Who receives it and where: a person added back with a newer email is a change,
// since a Send goes to the stored address.
const sameRecipient = (a: ProposalRecipient, b: ProposalRecipient) =>
  a.key === b.key &&
  a.role === b.role &&
  a.email === b.email &&
  a.name === b.name &&
  (a.personId ?? undefined) === (b.personId ?? undefined) &&
  (a.company ?? undefined) === (b.company ?? undefined) &&
  (a.jobTitle ?? undefined) === (b.jobTitle ?? undefined);

export const sameRecipients = (a: ProposalRecipients, b: ProposalRecipients) =>
  a.ordered === b.ordered &&
  a.list.length === b.list.length &&
  a.list.every((recipient, index) => sameRecipient(recipient, b.list[index]));

// Every recipient's request, as one status.
export const withRecipientStatus = (
  recipients: ProposalRecipients,
  status: (recipient: ProposalRecipient) => ProposalRecipientStatus | undefined
): ProposalRecipients => ({
  ...recipients,
  list: recipients.list.map((recipient) => {
    const next = { ...recipient, status: status(recipient) };
    if (next.status === undefined) delete next.status;
    return next;
  })
});

export const recipientsWithRole = (
  recipients: ProposalRecipients,
  role: ProposalRecipientRole
) => recipients.list.filter((recipient) => recipient.role === role);
